import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { icalConflictService } from '../services/icalConflictService';
import { useApartments } from './useApartments';
import { ConflictStatus, ResolveConflictRequest } from '../types/icalConflict';
import { AVAILABILITY_SOURCE_ATTRIBUTION_QUERY_KEY } from './useICalFeed';

export const ICAL_CONFLICTS_QUERY_KEY = 'icalConflicts';

export const icalConflictQueryKeys = {
  list: (apartmentId: string, status?: ConflictStatus) =>
    [ICAL_CONFLICTS_QUERY_KEY, apartmentId, status ?? null] as const,
};

/** Fetches conflicts for a single apartment, optionally scoped to `status`. */
export const useICalConflicts = (apartmentId: string, status?: ConflictStatus) =>
  useQuery({
    queryKey: icalConflictQueryKeys.list(apartmentId, status),
    queryFn: () => icalConflictService.listConflicts(apartmentId, status),
    enabled: !!apartmentId,
  });

/**
 * Mutation: resolve an active DOUBLE_BOOKING conflict with the given (request-vocabulary) outcome.
 * On success, invalidates: this apartment's conflicts query, bookings (cancel path changes booking
 * state), and availabilities/source-attribution (cancel path frees dates) — per research.md Decision 5.
 */
export const useResolveIcalConflict = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      apartmentId,
      conflictId,
      request,
    }: {
      apartmentId: string;
      conflictId: string;
      request: ResolveConflictRequest;
    }) => icalConflictService.resolveConflict(apartmentId, conflictId, request),
    onSuccess: async (_data, { apartmentId }) => {
      await queryClient.invalidateQueries({ queryKey: [ICAL_CONFLICTS_QUERY_KEY, apartmentId] });
      await queryClient.invalidateQueries({ queryKey: ['bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['availabilities', apartmentId] });
      await queryClient.invalidateQueries({
        queryKey: [AVAILABILITY_SOURCE_ATTRIBUTION_QUERY_KEY, apartmentId],
      });
    },
  });
};

export interface ActiveConflictSummary {
  readonly totalActiveCount: number;
  readonly byApartment: Record<string, number>;
}

/**
 * App-wide aggregate of active conflicts across every apartment the signed-in user is authorized
 * for (US4). Fans out to one `status=ACTIVE` query per apartment and reduces the results.
 *
 * NOTE: reconcile against a real backend summary endpoint if/when spec 053 adds one
 * (research.md Decision 4) — this client-side fan-out is a stopgap, not the final shape.
 */
export const useActiveConflictSummary = (): {
  readonly data: ActiveConflictSummary;
  readonly isLoading: boolean;
} => {
  const { data: apartments, isLoading: apartmentsLoading } = useApartments();
  const apartmentIds = apartments?.map(apartment => apartment.id) ?? [];

  const conflictQueries = useQueries({
    queries: apartmentIds.map(apartmentId => ({
      queryKey: icalConflictQueryKeys.list(apartmentId, 'ACTIVE' as ConflictStatus),
      queryFn: () => icalConflictService.listConflicts(apartmentId, 'ACTIVE'),
      enabled: !!apartmentId,
    })),
  });

  const isLoading = apartmentsLoading || conflictQueries.some(query => query.isLoading);

  const byApartment: Record<string, number> = {};
  let totalActiveCount = 0;

  apartmentIds.forEach((apartmentId, index) => {
    const count = conflictQueries[index]?.data?.length ?? 0;
    if (count > 0) {
      byApartment[apartmentId] = count;
    }
    totalActiveCount += count;
  });

  return { data: { totalActiveCount, byApartment }, isLoading };
};
